import React, { useCallback, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { useFieldDependentTable } from '../../../utils/metadata/tables';
import { useGlobalMetadata } from '../../../utils/providers';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { useAuth } from '../../../utils/useAuth';
import { insertRecord, updateRecord, deleteRecord, handleOperationError } from '../../../utils/supabase-operations';
import { invalidateAndRefreshAllQueriesForTable } from '../../../utils/metadata';

/**
 * Call Notes: an append-only-by-default log distinct from merchant.note, per
 * the RBAC spec's "call notes" write item (supabase/pg/rbac/call_notes.sql).
 * No ownership pre-check on add — same pattern as merchant note edits:
 * always show the form, let RLS reject and surface the error if the merchant
 * isn't the rep's own. Edit/delete ARE pre-checked client-side (author or
 * edit_deals) purely for UX (hide icons that would just 403); the DB RLS
 * (merchant_call_log_update/delete) is the actual boundary either way.
 */

interface CallLogRow {
  id: number;
  merchant: number;
  call_result: string | null;
  note: string | null;
  called_at: string;
  created_by: number | null;
  created_by_rel?: { name?: string } | null;
}

interface MerchantCallNotesTabProps {
  merchantId: number;
}

function MerchantCallNotesTab({ merchantId }: MerchantCallNotesTabProps) {
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability } = useMyCapabilities();
  const { appUser } = useAuth();
  const canEditAny = hasCapability('edit_deals');
  const myAppUserId = appUser?.id ?? null;

  const [adding, setAdding] = useState(false);
  const [callResult, setCallResult] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCallResult, setEditCallResult] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const picklistOptions: Array<{ value: string; label?: string }> =
    (metadata as any)?.picklistData?.merchant_call_log?.byFieldName?.call_result?.values || [];

  const conditions = useMemo(() => ({
    merchant: { filterType: 'number' as const, type: 'equals' as const, filter: merchantId, colId: 'merchant' },
  }), [merchantId]);

  const { data: logs, isLoading } = useFieldDependentTable({
    enabled: !!merchantId,
    tableName: 'merchant_call_log',
    schema: 'public',
    deep: true,
    conditions,
    orderBy: { column: 'called_at', direction: 'descending' },
  });

  const rows = (Array.isArray(logs) ? logs : logs ? [logs] : []) as unknown as CallLogRow[];

  const canEditRow = useCallback(
    (row: CallLogRow) => canEditAny || (myAppUserId != null && row.created_by === myAppUserId),
    [canEditAny, myAppUserId],
  );

  const resetForm = useCallback(() => {
    setAdding(false);
    setCallResult('');
    setNote('');
  }, []);

  const handleAdd = useCallback(async () => {
    if (!callResult && !note) return;
    setSaving(true);
    try {
      const { error } = await insertRecord({
        tableName: 'merchant_call_log',
        schema: 'public',
        data: {
          merchant: merchantId,
          call_result: callResult || null,
          note: note || null,
        },
        options: { invalidateCache: false },
      });
      if (error) throw error;
      await invalidateAndRefreshAllQueriesForTable('merchant_call_log');
      message.success('Call note added');
      resetForm();
    } catch (error) {
      const info = handleOperationError(error, 'Failed to add call note');
      message.error(info.userMessage);
    } finally {
      setSaving(false);
    }
  }, [callResult, note, merchantId, resetForm]);

  const startEdit = useCallback((row: CallLogRow) => {
    setEditingId(row.id);
    setEditCallResult(row.call_result || '');
    setEditNote(row.note || '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditCallResult('');
    setEditNote('');
  }, []);

  const handleSaveEdit = useCallback(async (id: number) => {
    setEditSaving(true);
    try {
      const result = await updateRecord({
        tableName: 'merchant_call_log',
        schema: 'public',
        data: { call_result: editCallResult || null, note: editNote || null },
        whereClause: { id },
        options: { invalidateCache: false },
      });
      if (result.error) throw result.error;
      await invalidateAndRefreshAllQueriesForTable('merchant_call_log');
      message.success('Saved');
      cancelEdit();
    } catch (error) {
      const info = handleOperationError(error, 'Failed to save call note');
      message.error(info.userMessage);
    } finally {
      setEditSaving(false);
    }
  }, [editCallResult, editNote, cancelEdit]);

  const handleDelete = useCallback(async (id: number) => {
    setRemovingId(id);
    try {
      const { error } = await deleteRecord({
        tableName: 'merchant_call_log',
        schema: 'public',
        whereClause: { id },
        options: { invalidateCache: false },
      });
      if (error) throw error;
      await invalidateAndRefreshAllQueriesForTable('merchant_call_log');
      message.success('Call note deleted');
    } catch (error) {
      const info = handleOperationError(error, 'Failed to delete call note');
      message.error(info.userMessage);
    } finally {
      setRemovingId(null);
    }
  }, []);

  if (isLoading) {
    return <div className="sky-mp-empty">Loading...</div>;
  }

  return (
    <div className="sky-panel-body">
      <div className="sky-mp-summary">
        <div className="sky-mp-summary-head">
          <span className="sky-mp-summary-label">CALL NOTES</span>
          {!adding && (
            <button className="sky-mp-new-btn" onClick={() => setAdding(true)}>+ Add Call Note</button>
          )}
        </div>

        {adding && (
          <div className="sky-mp-new-form" style={{ marginTop: 10 }}>
            <div className="sky-mp-new-form-row">
              <label className="sky-mp-new-form-label">
                Call Result
                <select
                  className="sky-field-input"
                  value={callResult}
                  onChange={(e) => setCallResult(e.target.value)}
                >
                  <option value="">—</option>
                  {picklistOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label || option.value}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="sky-mp-new-form-row">
              <label className="sky-mp-new-form-label">
                Note
                <textarea
                  className="sky-field-input sky-field-input--textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>
            <div className="sky-mp-new-form-actions">
              <button className="sky-mp-new-form-save" disabled={(!callResult && !note) || saving} onClick={() => void handleAdd()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="sky-mp-new-form-cancel" disabled={saving} onClick={resetForm}>Cancel</button>
            </div>
          </div>
        )}

        {rows.length === 0 && !adding && (
          <div className="sky-mp-empty-inline">No call notes logged yet.</div>
        )}

        {rows.length > 0 && (
          <div className="sky-mp-sub-list" style={{ marginTop: 10 }}>
            {rows.map((row) => {
              const isEditing = editingId === row.id;
              const editable = canEditRow(row);

              if (isEditing) {
                return (
                  <div key={row.id} className="sky-mp-call-note-row">
                    <div className="sky-mp-new-form-row">
                      <select
                        className="sky-field-input"
                        value={editCallResult}
                        onChange={(e) => setEditCallResult(e.target.value)}
                      >
                        <option value="">—</option>
                        {picklistOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label || option.value}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sky-mp-new-form-row">
                      <textarea
                        className="sky-field-input sky-field-input--textarea"
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                      />
                    </div>
                    <div className="sky-mp-new-form-actions">
                      <button className="sky-mp-new-form-save" disabled={editSaving} onClick={() => void handleSaveEdit(row.id)}>
                        {editSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button className="sky-mp-new-form-cancel" disabled={editSaving} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={row.id} className="sky-mp-call-note-row">
                  <div className="sky-mp-call-note-head">
                    <span className="sky-mp-call-note-result">{row.call_result || 'No result'}</span>
                    <div className="sky-mp-call-note-head-right">
                      <span className="sky-mp-call-note-meta">
                        {new Date(row.called_at).toLocaleString()}
                        {row.created_by_rel?.name ? ` • ${row.created_by_rel.name}` : ''}
                      </span>
                      {editable && (
                        <>
                          <button
                            className="sky-icon-btn"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                          </button>
                          <button
                            className="sky-icon-btn"
                            title="Delete"
                            disabled={removingId === row.id}
                            onClick={() => void handleDelete(row.id)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {row.note && <div className="sky-mp-call-note-text">{row.note}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(MerchantCallNotesTab);
